import { useTranslation } from 'helpers/hooks/useTranslation'
import { useRouter } from 'helpers/navigation'
import React from 'react'
import styled from 'styled-components'
import Button from 'uiComponents/atoms/Buttons/Button'
import ImageBox from 'uiComponents/atoms/ImageBox/ImageBox'
import { HeadingXS } from 'uiComponents/atoms/Text/Headings'
import { complementaryColor } from 'uiComponents/constants/colors'
import { fwMediumBold } from 'uiComponents/constants/typography'
import { StyledProductImage } from '../ProductImage/StyledProductImage'

const hoverTransitionTiming = '500ms ease'

const Shade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 100%;
  max-width: 100%;
  border-radius: 2px;
  background-color: rgba(0, 0, 0, 0);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color ${hoverTransitionTiming};
`

const StyledButton = styled(Button)`
  color: ${complementaryColor};
  background-color: transparent;
  border: 1px solid ${complementaryColor};
  opacity: 0;
  transition: all ${hoverTransitionTiming};
`

const TopEndContent = styled(({ component, ...props }) =>
  React.cloneElement(component, props),
)`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 2;
  opacity: 0;
  transition: opacity ${hoverTransitionTiming};
`

const IdolNameInside = styled(HeadingXS)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  padding: 30px 20px;
  color: ${complementaryColor};
  ${fwMediumBold};
  opacity: 0;
  transition: opacity ${hoverTransitionTiming};
`

const Wrapper = styled.a`
  cursor: pointer;
  display: block;
  overflow: hidden;
  position: relative;
  z-index: 0;
  background: ${complementaryColor};

  ${ImageBox} {
    transform-origin: center center;
    transition: transform ${hoverTransitionTiming};
  }

  &:hover {
    ${ImageBox} {
      transform: scale(1.05);
    }
    ${Shade} {
      background-color: rgba(0, 0, 0, 0.4);
    }
    ${StyledButton} {
      opacity: 1;
    }
    ${TopEndContent} {
      opacity: 1;
    }
    ${IdolNameInside} {
      opacity: 1;
    }
  }
`

const InfoContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px 50px;
`
const IdolName = styled(HeadingXS)`
  text-transform: uppercase;
  margin-bottom: 5px;
  padding-right: 10px;
`

interface Props {
  href: string
  imageSource: string
  idolName?: string
  className?: string
  topEndContent?: React.ReactNode
  onClick?: () => void
  disableContent?: boolean
}

const OutfitRecoTile = React.forwardRef<HTMLAnchorElement, Props>(
  function OutfitRecoTileComponent(
    {
      href,
      imageSource,
      idolName,
      className,
      topEndContent,
      onClick,
      disableContent,
    },
    ref,
  ) {
    const { t } = useTranslation()
    const { navigateClient } = useRouter()

    const onClickTile = (e: React.MouseEvent) => {
      if (onClick) {
        onClick()
      }
      navigateClient(href)(e)
    }

    return (
      <Wrapper
        data-testid="OutfitRecoTile"
        onClick={onClickTile}
        ref={ref}
        href={href}
        className={className}
      >
        <StyledProductImage data-testid="Image">
          {topEndContent && <TopEndContent component={topEndContent} />}
          <ImageBox src={imageSource} />
          <Shade>
            <StyledButton
              type="primary"
              onClick={onClickTile}
              data-testid="CTA"
            >
              {t('Shoppe das Outfit')}
            </StyledButton>
          </Shade>
          {disableContent && (
            <IdolNameInside data-testid="IdolName">{idolName}</IdolNameInside>
          )}
        </StyledProductImage>
        {!disableContent && (
          <InfoContent data-testid="InfoContent">
            <IdolName data-testid="IdolName">{idolName}</IdolName>
          </InfoContent>
        )}
      </Wrapper>
    )
  },
)

export default OutfitRecoTile
